import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { En, Fr, I18n } from "../I18n";
import regions from "../../regions";
// Single source of truth: parse the glossary directly from the prompt template
// synced from cioos-commons by firebase-functions/functions/generate-translation-config.js.
import promptTemplate from "../../../firebase-functions/functions/translation-prompt-template.txt?raw";

const TRANSLATION_SERVICE = "Cohere";
const TRANSLATION_MODEL = "command-a-translate-08-2025";

// Parse lines like:  - "English term" → "French term"
function parseGlossary(template) {
  const pairs = [];
  const lineRe = /^\s*-\s*"([^"]+)"\s*(?:→|->)\s*"([^"]+)"\s*$/gm;
  let match;
  while ((match = lineRe.exec(template)) !== null) {
    pairs.push([match[1], match[2]]);
  }
  return pairs;
}

const GLOSSARY = parseGlossary(promptTemplate);

const TranslationInfoDialog = ({ open, onClose }) => {
  const { region } = useParams();
  const regionEmail = regions?.[region]?.email;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <I18n>
          <En>About the translation service</En>
          <Fr>À propos du service de traduction</Fr>
        </I18n>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>
          <I18n>
            <En>Service and model</En>
            <Fr>Service et modèle</Fr>
          </I18n>
        </Typography>
        <Typography variant="body2" paragraph>
          <I18n>
            <En>
              Translations are generated by{" "}
              <strong>{TRANSLATION_SERVICE}</strong> using the model{" "}
              <strong>{TRANSLATION_MODEL}</strong>. A CIOOS-maintained glossary
              of domain-specific terms is included in every translation request
              to improve consistency with ocean-sector terminology.
            </En>
            <Fr>
              Les traductions sont générées par{" "}
              <strong>{TRANSLATION_SERVICE}</strong> à l'aide du modèle{" "}
              <strong>{TRANSLATION_MODEL}</strong>. Un glossaire de termes
              spécialisés maintenu par le SIOOC est inclus dans chaque requête
              de traduction afin d'améliorer la cohérence avec la terminologie
              du secteur océanique.
            </Fr>
          </I18n>
        </Typography>

        <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>
          <I18n>
            <En>Glossary translation corrections</En>
            <Fr>Corrections de traduction du glossaire</Fr>
          </I18n>
        </Typography>
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>English</strong>
                </TableCell>
                <TableCell>
                  <strong>Français</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {GLOSSARY.map(([en, fr]) => (
                <TableRow key={en}>
                  <TableCell>{en}</TableCell>
                  <TableCell>{fr}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
          <I18n>
            <En>
              Notice a recurring translation error? You can help improve the
              translation service for everyone by sharing common translation
              errors with CIOOS. We will review reported terms and add
              corrections to the shared glossary
              {regionEmail ? (
                <>
                  {" "}
                  — please email{" "}
                  <a
                    href={`mailto:${regionEmail}?subject=Translation%20glossary%20suggestion`}
                  >
                    {regionEmail}
                  </a>
                  .
                </>
              ) : (
                "."
              )}
            </En>
            <Fr>
              Vous remarquez une erreur de traduction récurrente ? Vous pouvez
              aider à améliorer le service de traduction pour tous en partageant
              les erreurs courantes avec le SIOOC. Nous examinerons les termes
              signalés et ajouterons les corrections au glossaire partagé
              {regionEmail ? (
                <>
                  {" "}
                  — veuillez écrire à{" "}
                  <a
                    href={`mailto:${regionEmail}?subject=Suggestion%20de%20glossaire%20de%20traduction`}
                  >
                    {regionEmail}
                  </a>
                  .
                </>
              ) : (
                "."
              )}
            </Fr>
          </I18n>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <I18n>
            <En>Close</En>
            <Fr>Fermer</Fr>
          </I18n>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslationInfoDialog;
